/**
 * HTTP请求拦截器 - 自动处理token刷新
 * 确保token失效后只刷新一次，刷新成功后回调所有等待的请求
 */
import axios from 'axios';
import pub from '../service/public';
import cookie from '../service/cookie';
import config from '../config';
import router from '../router';

// 创建axios实例
const httpClient = axios.create();

// 请求拦截器
httpClient.interceptors.request.use(
  (config) => {
    // 只有非排除的URL才添加token
    if (!isUrlExcluded(config.url || '')) {
      const token = pub.getToken();
      if (token) {
        config.headers.Authorization = token;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
httpClient.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config;

    // 检查是否是token相关的业务错误码
    if (isTokenExpiredBusinessCode(response) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // 等待token刷新完成
        await waitForTokenRefresh();

        // 更新请求头中的token
        const newToken = pub.getToken();
        if (newToken) {
          originalRequest.headers.Authorization = newToken;
        }

        // 重新发送原始请求
        return httpClient(originalRequest);
      } catch (refreshError) {
        // token刷新失败，跳转到登录页
        console.error('Token刷新失败:', refreshError);
        toLogin();
        return Promise.reject();
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 检查是否是HTTP状态码token错误
    if (isHttpStatusTokenError(error) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // 等待token刷新完成
        await waitForTokenRefresh();

        // 更新请求头中的token
        const newToken = pub.getToken();
        if (newToken) {
          originalRequest.headers.Authorization = newToken;
        }

        // 重新发送原始请求
        return httpClient(originalRequest);
      } catch (refreshError) {
        // token刷新失败，跳转到登录页
        console.error('Token刷新失败:', refreshError);
        toLogin();
        return Promise.reject(error);
      }
    }
    // 非 token 相关的 HTTP 错误：在这里统一处理 404 / 500
    if (error.response) {
      const status = error.response.status;
      if (status === 404) {
        const normalized = {
          ...error.response,
          data: {
            code: 404,
            message: '接口未定义，请联系相关人员'
          }
        };
        pub.tip(normalized.data.message);
        // 将规范化后的响应作为成功结果返回，避免业务层进入 catch
        return Promise.resolve(normalized);
      }
      if (status === 500) {
        const normalized = {
          ...error.response,
          data: {
            code: 500,
            message: '服务器错误，请联系相关人员'
          }
        };
        pub.tip(normalized.data.message);
        return Promise.resolve(normalized);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * 检查URL是否被排除在token验证之外
 */
function isUrlExcluded(url) {
  const excludedUrls = [
    '/Usr/UsrLogin',  // 登录
    '/Usr/UsrLogout'  // 登出
  ];
  return excludedUrls.some(excludedUrl => url.includes(excludedUrl));
}

/**
 * 检查是否是HTTP状态码token错误
 */
function isHttpStatusTokenError(error) {
  if (!error.response) return false;

  const status = error.response.status;
  const originalRequest = error.config;

  // 检查URL是否被排除
  if (originalRequest && originalRequest.url && isUrlExcluded(originalRequest.url)) {
    return false;
  }

  // HTTP状态码相关
  return status === 401 || status === 403;
}

/**
 * 检查是否是业务错误码token过期
 */
function isTokenExpiredBusinessCode(response) {
  if (!response || !response.data || !response.config) return false;

  const data = response.data;
  const originalRequest = response.config;

  // 检查URL是否被排除
  if (originalRequest.url && isUrlExcluded(originalRequest.url)) {
    return false;
  }

  // 排除token刷新接口本身，避免无限循环
  if (originalRequest.url && originalRequest.url.includes('/Usr/RefreshToken')) {
    return false;
  }

  // 业务状态码相关
  if (data && data.code) {
    // 仅当 code 为全局定义的 TOKEN_TIMEOUT(1283) 时视作 token 过期并静默续期；
    // 1281(token不存在) / 1282(token校验失败) 不续期，统一由业务层提示并退出到登录页
    return data.code === pub.backCode.TOKEN_TIMEOUT;
  }

  return false;
}

/**
 * 跳转到登录页（token 刷新失败时）
 */
function toLogin() {
  pub.tip('登录信息已失效，请重新登录');
  pub.clearLoginState();
  pub.goLogin(router.currentRoute.fullPath);
}

/**
 * 等待token刷新完成
 */
function waitForTokenRefresh() {
  return new Promise((resolve, reject) => {
    // 初始化 tokenRefreshState（如果不存在）
    if (!pub.tokenRefreshState) {
      pub.tokenRefreshState = {
        isRefreshing: false,
        refreshPromise: null,
        waitingRequests: []
      };
    }

    // 如果正在刷新，等待刷新完成
    if (pub.tokenRefreshState.isRefreshing) {
      // 等待现有的刷新Promise完成
      if (pub.tokenRefreshState.refreshPromise) {
        pub.tokenRefreshState.refreshPromise
          .then(() => resolve())
          .catch((error) => reject(error));
      } else {
        // 如果没有refreshPromise，说明刷新状态异常，直接拒绝
        reject(new Error('Token刷新状态错误'));
      }
    } else {
      // 开始刷新token
      pub.tokenRefreshState.isRefreshing = true;

      let refresh_token = pub.getRefreshToken();
      // 临时以 refreshToken 作为请求凭证，双写 localStorage + cookie 镜像
      pub.saveLoginState(refresh_token, '');

      const apiRoot = config.getApiRootSync();
      // 添加超时处理，5秒超时后直接跳转登录页
      const timeoutPromise = new Promise((_, timeoutReject) => {
        setTimeout(() => {
          timeoutReject(new Error('Token刷新超时'));
        }, 5000);
      });

      pub.tokenRefreshState.refreshPromise = Promise.race([
        axios.post(apiRoot + '/Usr/RefreshToken', {}, {
          headers: {
            'Authorization': pub.getToken()
          }
        }),
        timeoutPromise
      ]);

      pub.tokenRefreshState.refreshPromise
        .then(function (res) {
          if (res.data.code === pub.backCode.SUCCESS) {
            // 刷新成功，更新token（双写 localStorage + cookie 镜像）
            if (res.data.data && res.data.data.strAuthorization) {
              pub.saveLoginState(res.data.data.strAuthorization, '');
            }

            // 执行所有等待队列中的请求回调
            if (pub.tokenRefreshState.waitingRequests) {
              pub.tokenRefreshState.waitingRequests.forEach(waitingRequest => {
                if (waitingRequest.callBack) {
                  waitingRequest.callBack(waitingRequest.param);
                }
                if (waitingRequest.resolve) {
                  waitingRequest.resolve();
                }
              });
            }

            // 清空等待队列
            pub.tokenRefreshState.waitingRequests = [];

            resolve();
          } else {
            // 刷新失败，清空token并跳转登录页
            cookie.setCookie(pub.token.token_key, '');
            cookie.setCookie(pub.token.refresh_key, '');
            toLogin();
            reject(new Error('登录信息过期，请重新登录'));
          }
        })
        .catch(function (error) {
          console.log('Token刷新错误:', error);
          // 刷新超时或失败，直接跳转登录页
          toLogin();
          reject(error);
        })
        .finally(() => {
          // 重置刷新状态
          pub.tokenRefreshState.isRefreshing = false;
          pub.tokenRefreshState.refreshPromise = null;
          pub.tokenRefreshState.waitingRequests = [];
        });
    }
  });
}

export default httpClient;



// WEBPACK FOOTER //
// ./src/request/requestInterceptor.js